import { useMemo } from 'react';
import { useActiveFBOs } from '../containers/ActiveFBOContainer';
import { useIdentity } from '../containers/IdentityContainer';
import {
  hasPermission,
  isInViewOnlyMode,
  canModifyAircraftPreferences,
  canModifyLayout,
  canManageTeamMembers,
  canModifyTenants,
  canUseFavorites,
  canAccessSuperAdmin,
  hasSettingsAccess,
  getUserRole,
  Permission,
  UserRole,
  PermissionContext
} from '../utils/permissions';

/**
 * Hook for checking user permissions throughout the application
 *
 * Provides a memoized permission context and convenient permission checking functions.
 * This hook consolidates permission logic that was previously scattered across components.
 *
 * @returns Object with permission checking functions and user role information
 */
export function usePermissions() {
  const { activeFBO } = useActiveFBOs();
  const { airplxIdentity } = useIdentity();

  const context: PermissionContext = useMemo(() => ({
    user: {
      isAdmin: airplxIdentity.isadmin,
      userid: airplxIdentity.userid
    },
    fbo: activeFBO
  }), [airplxIdentity, activeFBO]);

  return {
    // Core permission checking
    hasPermission: (permission: Permission) => hasPermission(permission, context),

    // Role information
    userRole: getUserRole(context.user, context.fbo),
    isSystemAdmin: context.user.isAdmin,

    // Common permission helpers
    isViewOnlyMode: () => isInViewOnlyMode(context),
    canModifyAircraftPreferences: () => canModifyAircraftPreferences(context),
    canModifyLayout: () => canModifyLayout(context),
    canManageTeamMembers: () => canManageTeamMembers(context),
    canModifyTenants: () => canModifyTenants(context),
    canUseFavorites: () => canUseFavorites(context),
    canAccessSettings: () => hasSettingsAccess(context),

    // Direct permission checks (for less common permissions)
    canAccessSuperAdmin: () => canAccessSuperAdmin(context),

    // Permission context for advanced use cases
    context,
  };
}